import React, { useEffect, useState } from "react";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from "@mui/material";
import { getCounsellors } from "../marketing/marketingApi";
import { getIstTodayYmd, addDaysYmd } from "../../utils/datetimeIst";

const PRESETS = ["Today", "Yesterday", "Weekly", "Monthly", "Quarterly", "Custom"];

/** @typedef {{ year: number, quarter: 1 | 2 | 3 | 4 }} QuarterlySelection */

const QUARTER_OPTIONS = [
  { quarter: 1, label: "Q1 (Jan–Mar)" },
  { quarter: 2, label: "Q2 (Apr–Jun)" },
  { quarter: 3, label: "Q3 (Jul–Sep)" },
  { quarter: 4, label: "Q4 (Oct–Dec)" },
];

function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function ymdToUtcDate(ymd) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** First day of quarter and last day of quarter (calendar). quarter 1–4. */
function getQuarterBounds(year, quarter) {
  const startMonths = [0, 3, 6, 9];
  const m0 = startMonths[quarter - 1];
  const start = new Date(year, m0, 1);
  const end = new Date(year, m0 + 3, 0);
  return { start, end };
}

/**
 * Date range for dashboard: past quarters = full 3 months; current quarter = start through today;
 * future quarters = full quarter (for consistency if year is picked ahead).
 */
export function getQuarterlyDateRange(year, quarter, refDate = new Date()) {
  const { start, end } = getQuarterBounds(year, quarter);
  const todayYmd = getIstTodayYmd();
  const today = ymdToUtcDate(todayYmd);
  const startNorm = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endNorm = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  if (today < startNorm) {
    return { start: startNorm, end: endNorm };
  }
  if (today > endNorm) {
    return { start: startNorm, end: endNorm };
  }
  return { start: startNorm, end: today };
}

/**
 * @param {string} preset
 * @param {string} [customStart]
 * @param {string} [customEnd]
 * @param {QuarterlySelection | null} [quarterly] - for "Quarterly"; omit = current quarter of current year (by refDate)
 * @param {Date} [refDate] - unused for day math; IST "today" is used instead
 */
export function getDateRangeFromPreset(preset, customStart, customEnd, quarterly = null, refDate = new Date()) {
  const todayYmd = getIstTodayYmd();
  const [ty, tm] = todayYmd.split("-").map(Number);
  let startYmd = todayYmd;
  let endYmd = todayYmd;

  switch (preset) {
    case "Today":
      break;
    case "Yesterday":
      startYmd = endYmd = addDaysYmd(todayYmd, -1);
      break;
    case "Weekly": {
      // Sunday-start week in IST calendar, through today
      const [y, m, d] = todayYmd.split("-").map(Number);
      const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun
      startYmd = addDaysYmd(todayYmd, -dow);
      endYmd = todayYmd;
      break;
    }
    case "Monthly":
      startYmd = `${ty}-${String(tm).padStart(2, "0")}-01`;
      endYmd = todayYmd;
      break;
    case "Quarterly": {
      const y = quarterly?.year ?? ty;
      const q = quarterly?.quarter ?? Math.floor((tm - 1) / 3) + 1;
      const range = getQuarterlyDateRange(y, q, ymdToUtcDate(todayYmd));
      return { start_date: toISO(range.start), end_date: toISO(range.end) };
    }
    case "Custom":
      if (customStart && customEnd) {
        return {
          start_date: String(customStart).slice(0, 10),
          end_date: String(customEnd).slice(0, 10),
        };
      }
      return null;
    default:
      return null;
  }
  return { start_date: startYmd, end_date: endYmd };
}

function defaultQuarterlySelection() {
  const todayYmd = getIstTodayYmd();
  const [y, m] = todayYmd.split("-").map(Number);
  return { year: y, quarter: Math.floor((m - 1) / 3) + 1 };
}

export default function DashboardFilters({ value, onChange, hideCounsellor = false }) {
  const { start_date, end_date, counsellor_id } = value || {};
  const [preset, setPreset] = useState("Monthly");
  const [customStart, setCustomStart] = useState(start_date || "");
  const [customEnd, setCustomEnd] = useState(end_date || "");
  const [quarterlySelection, setQuarterlySelection] = useState(defaultQuarterlySelection);
  const [counsellors, setCounsellors] = useState([]);

  useEffect(() => {
    if (hideCounsellor) return;
    getCounsellors()
      .then((list) => setCounsellors(list || []))
      .catch(() => setCounsellors([]));
  }, [hideCounsellor]);

  const handlePreset = (p) => {
    setPreset(p);
    if (p !== "Custom") {
      const range =
        p === "Quarterly"
          ? getDateRangeFromPreset("Quarterly", undefined, undefined, quarterlySelection)
          : getDateRangeFromPreset(p);
      if (range) onChange({ ...value, ...range });
    }
  };

  const handleQuarterlyYear = (year) => {
    const y = Number(year);
    setQuarterlySelection((prev) => ({ ...prev, year: y }));
    if (preset === "Quarterly") {
      const range = getDateRangeFromPreset("Quarterly", undefined, undefined, { year: y, quarter: quarterlySelection.quarter });
      if (range) onChange({ ...value, ...range });
    }
  };

  const handleQuarterlyQuarter = (q) => {
    const quarter = Number(q);
    setQuarterlySelection((prev) => ({ ...prev, quarter }));
    if (preset === "Quarterly") {
      const range = getDateRangeFromPreset("Quarterly", undefined, undefined, { year: quarterlySelection.year, quarter });
      if (range) onChange({ ...value, ...range });
    }
  };

  const handleCustomApply = () => {
    const range = getDateRangeFromPreset("Custom", customStart, customEnd);
    if (range) onChange({ ...value, ...range });
  };

  const handleCounsellor = (e) => {
    const id = e.target.value === "" || e.target.value === "all" ? undefined : Number(e.target.value);
    onChange({ ...value, counsellor_id: id });
  };

  return (
    <Stack direction="column" spacing={2} width="100%" useFlexGap>
      {/* Row 1: horizontal-scroll quick presets + counsellor */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        width="100%"
        useFlexGap
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              gap: 1,
              p: 0.5,
              borderRadius: 99,
              bgcolor: "var(--table-header, #f8fafc)",
              border: "1px solid var(--border-color, #e5e7eb)",
              overflowX: "auto",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { height: 6 },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 99,
                backgroundColor: "var(--border-color, #cbd5e1)",
              },
            }}
          >
            {PRESETS.map((p) => (
              <Button
                key={p}
                onClick={() => handlePreset(p)}
                disableElevation
                size="small"
                variant={preset === p ? "contained" : "text"}
                sx={{
                  flexShrink: 0,
                  borderRadius: 99,
                  minWidth: 96,
                  px: 1.75,
                  py: 0.75,
                  fontWeight: 600,
                  textTransform: "none",
                  color: preset === p ? "var(--button-primary-text, #fff)" : "var(--text-secondary, #6b7280)",
                  bgcolor: preset === p ? "var(--button-primary, #5e72e4)" : "transparent",
                  "&:hover": {
                    bgcolor: preset === p ? "var(--button-primary-hover, #4c5fd6)" : "var(--accent-muted, rgba(94,114,228,0.12))",
                  },
                }}
              >
                {p}
              </Button>
            ))}
          </Box>
        </Box>
        {!hideCounsellor && (
          <FormControl
            size="small"
            sx={{
              minWidth: 240,
              width: { xs: "100%", sm: "auto" },
              flexShrink: 0,
            }}
          >
            <InputLabel>Counsellor</InputLabel>
            <Select
              label="Counsellor"
              value={counsellor_id ?? "all"}
              onChange={handleCounsellor}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="all">All Counsellors</MenuItem>
              {counsellors.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name || c.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>

      {/* Row 2: quarterly / custom — full width below quick selector */}
      {preset === "Quarterly" && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap width="100%">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Quarter</InputLabel>
            <Select
              label="Quarter"
              value={quarterlySelection.quarter}
              onChange={(e) => handleQuarterlyQuarter(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              {QUARTER_OPTIONS.map((opt) => (
                <MenuItem key={opt.quarter} value={opt.quarter}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              label="Year"
              value={quarterlySelection.year}
              onChange={(e) => handleQuarterlyYear(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - 5 + i).map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      )}
      {preset === "Custom" && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          flexWrap="wrap"
          useFlexGap
          width="100%"
        >
          <TextField
            label="Start"
            type="date"
            size="small"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: "100%", sm: 150 } }}
          />
          <TextField
            label="End"
            type="date"
            size="small"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: "100%", sm: 150 } }}
          />
          <Button variant="contained" size="small" onClick={handleCustomApply} sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}>
            Apply
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
